import * as React from "react";

type FallbackRenderArgs = {
  error: Error;
  reset: () => void;
};

type ErrorBoundaryProps = {
  /** A React node or a render function that receives { error, reset } */
  fallback: React.ReactNode | ((args: FallbackRenderArgs) => React.ReactNode);
  /** Called when an error is caught */
  onError?: (error: Error, info: React.ErrorInfo) => void;
  /** Called when boundary is reset (e.g., by reset button or resetKeys change) */
  onReset?: () => void;
  /**
   * When any of these values change (shallow-compare), the boundary resets itself.
   * Useful to reset on route change, tab change, etc.
   */
  resetKeys?: Array<unknown>;
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  // Set error state for render fallback
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  // Report error side-effect
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info);
  }

  // Reset boundary when resetKeys change
  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (!areArraysShallowEqual(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    const { hasError, error } = this.state;
    const { fallback } = this.props;

    if (hasError && error) {
      if (typeof fallback === "function") {
        return (fallback as (args: FallbackRenderArgs) => React.ReactNode)({
          error,
          reset: this.reset,
        });
      }
      return fallback;
    }

    return this.props.children;
  }
}

// Small util for resetKeys shallow comparison
function areArraysShallowEqual(a?: unknown[], b?: unknown[]) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (Object.is(a[i], b[i]) === false) return false;
  }
  return true;
}

/** Optional HOC for convenience */
export function withErrorBoundary<P>(
  Component: React.ComponentType<P>,
  boundaryProps: Omit<ErrorBoundaryProps, "children">
) {
  return function Wrapped(props: P) {
    return (
      <ErrorBoundary {...boundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}
